import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { Auth0Provider } from '@auth0/auth0-react';
import axios from 'axios';
import * as Sentry from '@sentry/react';
import App from './App';
import { Toaster } from '@/app/ui/sonner';
import { TooltipProvider } from '@/app/ui/tooltip';
import './css/index.css';
import GoogleMapsLoader from '@/app/googleMaps/GoogleMapsLoader';
import { getAuth0Domain, getAuth0ClientId, getAuth0RedirectUri, getAuth0Audience } from '@/helpers/auth0';
import ThemeProvider from '@/app/theme/ThemeProvider';
import '@fontsource-variable/inter';
import '@fontsource-variable/source-code-pro';
import { getApiUrl } from '@/helpers/api';
import MixpanelProvider from '@/app/mixpanel/MixpanelProvider';

Sentry.init({
  // Enabled only in production - local and e2e is off
  enabled: process.env.SENTRY_ENABLED,
  dsn: 'https://7a4b5295632488af4d41e34c038fc0cd@o4511418138820608.ingest.us.sentry.io/4511427920068608',
  // PII such as the user's IP address is collected by default (`dataCollection`), which matches the
  // `sendDefaultPii: true` we set before v11.
  integrations: [
    Sentry.replayIntegration(),
    // Sending logs needs no `enableLogs` since v11: adding this integration is what turns them on
    Sentry.consoleLoggingIntegration({ levels: ['log', 'warn', 'error'] }),
    // v11 defaults to one session per page load; keep a new session on every navigation, as before
    Sentry.browserSessionIntegration({ lifecycle: 'route' })
  ],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0 // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.,
});

axios.defaults.withCredentials = false;
axios.defaults.baseURL = getApiUrl();

const container = document.getElementById('root');
const root = createRoot(container);

root.render(
  <MixpanelProvider>
    <Auth0Provider
      domain={getAuth0Domain()}
      clientId={getAuth0ClientId()}
      authorizationParams={{
        redirect_uri: getAuth0RedirectUri(),
        audience: getAuth0Audience()
      }}
    >
      <ThemeProvider>
        <BrowserRouter>
          <GoogleMapsLoader>
            <TooltipProvider>
              <App />
              <Toaster />
            </TooltipProvider>
          </GoogleMapsLoader>
        </BrowserRouter>
      </ThemeProvider>
    </Auth0Provider>
  </MixpanelProvider>
);
